import React, { useState, useEffect } from 'react';
import { FL_SCRIPT_LABELS } from '../shared/fiveLeague';
import { submitQuestion, getCategories } from '../shared/questionsTx';
import { auth, isEmailUser } from '../firebase';
import { NeonText, CyberButton, GlowingBorder } from './CyberEffects';
import './QuestionSubmitModal.css';

// フォールバック用デフォルトカテゴリ
const DEFAULT_CATEGORIES = [
  '動物', '食べ物', '地理', '歴史', '科学', '文化',
  'スポーツ', '音楽', '自然', '乗り物', '数学', '天気',
];

function QuestionSubmitModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    category: '動物',
    customCategory: '',
    difficulty: 2,
    length: 4,
    script: 'hiragana',
    answer: ['', '', '', ''],
    prompt: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  // カテゴリをFirestoreから取得
  useEffect(() => {
    if (isOpen) {
      getCategories().then(result => {
        if (result.ok && result.categories) {
          setCategories(result.categories);
        }
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleLengthChange = (newLength) => {
    const len = parseInt(newLength);
    const newAnswer = [...formData.answer];
    while (newAnswer.length < len) newAnswer.push('');
    while (newAnswer.length > len) newAnswer.pop();
    setFormData({ ...formData, length: len, answer: newAnswer });
  };

  const handleAnswerChange = (idx, value) => {
    const newAnswer = [...formData.answer];
    newAnswer[idx] = value;
    setFormData({ ...formData, answer: newAnswer });
  };

  const getFinalCategory = () => {
    if (formData.category === 'その他') {
      return formData.customCategory.trim();
    }
    return formData.category;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const finalCategory = getFinalCategory();

    // バリデーション
    if (!finalCategory) {
      setError('カテゴリを選択または入力してください');
      return;
    }
    if (!formData.prompt.trim()) {
      setError('問題文を入力してください');
      return;
    }
    if (formData.answer.some(a => !a.trim())) {
      setError('すべての答えを入力してください');
      return;
    }

    // 認証チェック
    if (!auth.currentUser) {
      setError('ログインが必要です');
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitQuestion({
        category: finalCategory,
        difficulty: formData.difficulty,
        length: formData.length,
        script: formData.script,
        answer: formData.answer.map(a => a.trim()),
        prompt: formData.prompt.trim(),
      });

      if (result.ok) {
        setSuccess(true);
        // 3秒後にリセット
        setTimeout(() => {
          setSuccess(false);
          setFormData({
            category: '動物',
            customCategory: '',
            difficulty: 2,
            length: 4,
            script: 'hiragana',
            answer: ['', '', '', ''],
            prompt: '',
          });
        }, 3000);
      } else {
        setError('投稿に失敗しました: ' + result.reason);
      }
    } catch (e) {
      setError('エラーが発生しました: ' + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess(false);
    onClose();
  };

  return (
    <div className="question-submit-overlay" onClick={handleClose}>
      <div className="question-submit-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>×</button>

        <h2 className="modal-title">
          <NeonText color="accent" size="lg">問題を投稿</NeonText>
        </h2>

        {!isEmailUser() && (
          <div className="login-notice">
            <p>メールアドレスでログインすると、投稿した問題のステータスを確認できます</p>
          </div>
        )}

        {success ? (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <p>問題を投稿しました！</p>
            <p className="success-note">管理者の承認後に公開されます</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="submit-form">
            <div className="form-row">
              <label>カテゴリ</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
                <option value="その他">その他（自由入力）</option>
              </select>
            </div>

            {formData.category === 'その他' && (
              <div className="form-row">
                <label>カテゴリ名（自由入力）</label>
                <input
                  type="text"
                  value={formData.customCategory}
                  onChange={(e) => setFormData({ ...formData, customCategory: e.target.value })}
                  placeholder="例: 芸能、アニメ"
                  maxLength={20}
                />
              </div>
            )}

            <div className="form-row-group">
              <div className="form-row">
                <label>文字数</label>
                <select
                  value={formData.length}
                  onChange={(e) => handleLengthChange(e.target.value)}
                >
                  <option value="3">3文字</option>
                  <option value="4">4文字</option>
                  <option value="5">5文字</option>
                  <option value="6">6文字</option>
                </select>
              </div>

              <div className="form-row">
                <label>難易度</label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: parseInt(e.target.value) })}
                >
                  <option value="1">1 (かんたん)</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5 (超難問)</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <label>文字種</label>
              <select
                value={formData.script}
                onChange={(e) => setFormData({ ...formData, script: e.target.value })}
              >
                <option value="hiragana">ひらがな</option>
                <option value="katakana">カタカナ</option>
                <option value="kanji">漢字</option>
                <option value="number">半角数字</option>
                <option value="alphabet">アルファベット大文字</option>
              </select>
            </div>

            <div className="form-row">
              <label>問題文</label>
              <textarea
                value={formData.prompt}
                onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                placeholder="答えが一意に特定できる説明を入力&#10;（答えの文字数は含めないでください）"
                rows={3}
                maxLength={200}
              />
            </div>

            <div className="form-row answer-section">
              <label>答え（{formData.length}文字）</label>
              <div className="answer-inputs">
                {formData.answer.map((char, idx) => (
                  <input
                    key={idx}
                    type="text"
                    className="answer-char-input"
                    value={char}
                    onChange={(e) => handleAnswerChange(idx, e.target.value.slice(0, 1))}
                    maxLength={1}
                    placeholder={String(idx + 1)}
                  />
                ))}
              </div>
              <div className="answer-preview">
                答え: <strong>{formData.answer.map(c => c || '_').join('')}</strong>
              </div>
            </div>

            {error && <p className="error-message">{error}</p>}

            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={handleClose}>
                キャンセル
              </button>
              <CyberButton
                type="submit"
                variant="primary"
                disabled={submitting}
              >
                {submitting ? '投稿中...' : '投稿する'}
              </CyberButton>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default QuestionSubmitModal;
